/**
 * Le anomalie della commessa, in elenco (GI-237).
 *
 * **La coda non e' un elenco.** La coda porta chi aspetta un verdetto, uno
 * alla volta: e' lo strumento di chi decide. Chi sorveglia la commessa chiede
 * un'altra cosa — che cosa c'e' sulla linea, che cosa e' stato deciso, che
 * cosa manca — ed e' la scheda «Anomalie» del vecchio prototipo, che il
 * prodotto aveva nascosto dietro la coda.
 *
 * **Due colonne, la pagina non scorre.** A sinistra i filtri e la tabella,
 * che scorre dentro la sua colonna; a destra la scheda della riga scelta, con
 * la miniatura e le uscite: il banco per decidere o rileggere, la scheda
 * dell'elemento, il fotogramma fra le foto.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { ServiceError } from '../api'
import { Severity } from '../design/indicators'
import { EmptyState } from '../carta/EmptyState'
import { LineMap } from '../carta/LineMap'
import { bandOf, marginOf, rankOf } from '../design/scales'
import { decimal, moment } from '../format'
import { useT } from '../i18n/LocaleProvider'
import { explainReason, explainService } from '../i18n/explain'
import { outbox } from '../outbox'
import type { Rejected } from '../outbox'
import * as reviews from '../reviews'
import { openSession, prelease } from '../reviewSessions'
import type { Reason } from '../types'
import type { MessageKey } from '../i18n/messages'
import * as api from './api'
import type { AnomalyFilters, AnomalyRow, AnomalyStatus } from './api'
import './anomalies.css'

export interface AnomaliesViewProps {
  inspectionId: string
}

const STATUSES: { id: AnomalyStatus | null; key: MessageKey; count: keyof api.AnomalyCounts }[] = [
  { id: null, key: 'anomalies.all', count: 'total' },
  { id: 'waiting', key: 'anomalies.waiting', count: 'waiting' },
  { id: 'confirmed', key: 'anomalies.confirmed', count: 'confirmed' },
  { id: 'rejected', key: 'anomalies.rejected', count: 'rejected' },
]

export function AnomaliesView({ inspectionId }: AnomaliesViewProps): JSX.Element {
  const t = useT()
  const [found, setFound] = useState<api.Anomalies | null>(null)
  const [failure, setFailure] = useState<ServiceError | null>(null)
  const [filters, setFilters] = useState<AnomalyFilters>({ status: null, rank: null, q: null })
  const [typed, setTyped] = useState('')
  const [offset, setOffset] = useState(0)
  const [chosen, setChosen] = useState<string | null>(null)
  // Il pacchetto (GI-252): le righe spuntate, il motivo, e la finestra di
  // annullamento. Solo il rifiuto si fa a pacchetto — la conferma no: nessun
  // dato si consegna senza che un occhio l'abbia guardato uno per uno.
  const [ticked, setTicked] = useState<Set<string>>(() => new Set())
  const [reasons, setReasons] = useState<Reason[]>([])
  const [reason, setReason] = useState('')
  const [batch, setBatch] = useState<{ rejected: string[]; skipped: number; undoUntil: number } | null>(null)
  const [batchFailure, setBatchFailure] = useState<string | null>(null)
  const [refusals, setRefusals] = useState<Rejected[]>([])
  const [reloads, setReloads] = useState(0)
  const lastTick = useRef<string | null>(null)
  const live = useRef<{ sessionId: string; reviewEpoch: number } | null>(null)
  const posta = useMemo(
    () =>
      outbox({
        onChange: () => undefined,
        onRejected: (one) => setRefusals((were) => [...were, one]),
        session: () => live.current,
      }),
    [],
  )
  useEffect(() => {
    reviews.reasons().then(setReasons).catch(() => setReasons([]))
  }, [])
  useEffect(() => {
    if (batch === null) return
    const left = batch.undoUntil - Date.now()
    const timer = window.setTimeout(() => {
      setBatch(null)
      setReloads((was) => was + 1)
    }, Math.max(0, left))
    return () => window.clearTimeout(timer)
  }, [batch])

  useEffect(() => {
    let alive = true
    api
      .anomalies(inspectionId, { ...filters, offset })
      .then((got) => {
        if (!alive) return
        setFound((were) =>
          offset === 0 || were === null ? got : { ...got, rows: [...were.rows, ...got.rows] },
        )
      })
      .catch((error: unknown) => {
        if (alive && error instanceof ServiceError) setFailure(error)
      })
    return () => {
      alive = false
    }
  }, [inspectionId, filters, offset, reloads])

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = typed.trim() === '' ? null : typed.trim()
      setFilters((were) => (were.q === q ? were : { ...were, q }))
      setOffset(0)
    }, 250)
    return () => clearTimeout(timer)
  }, [typed])

  function filter(change: Partial<AnomalyFilters>): void {
    setFilters((were) => ({ ...were, ...change }))
    setOffset(0)
  }

  const rows = found?.rows ?? []
  const current = useMemo(
    () => rows.find((one) => one.detection_id === chosen) ?? null,
    [rows, chosen],
  )

  if (failure) {
    return (
      <main>
        <h2>{t('anomalies.unread')}</h2>
        <p role="alert">{failure.message}</p>
      </main>
    )
  }

  const counts = found?.counts ?? { total: 0, waiting: 0, confirmed: 0, rejected: 0 }
  function tick(row: api.AnomalyRow, withShift: boolean): void {
    if (row.status !== 'waiting') return
    setTicked((were) => {
      const next = new Set(were)
      // Con Maiuscolo si spunta l'intervallo dall'ultima spunta a questa:
      // dieci doppioni in fila sono un gesto, non dieci.
      const from = withShift && lastTick.current !== null ? rows.findIndex((one) => one.detection_id === lastTick.current) : -1
      const to = rows.findIndex((one) => one.detection_id === row.detection_id)
      const span = from >= 0 && to >= 0 ? rows.slice(Math.min(from, to), Math.max(from, to) + 1) : [row]
      const turningOn = !were.has(row.detection_id)
      for (const one of span) {
        if (one.status !== 'waiting') continue
        if (turningOn) next.add(one.detection_id)
        else next.delete(one.detection_id)
      }
      return next
    })
    lastTick.current = row.detection_id
  }

  async function rejectTicked(): Promise<void> {
    if (ticked.size === 0 || reason === '') return
    setBatchFailure(null)
    try {
      const opened = await openSession(inspectionId)
      live.current = { sessionId: opened.review_session_id, reviewEpoch: opened.review_epoch }
      const wanted = [...ticked].slice(0, opened.max_active_leases)
      const taken = await prelease(opened.review_session_id, wanted.length, wanted)
      for (const lease of taken.leases) {
        posta.add({
          kind: 'reject',
          detectionId: lease.detection_id,
          reasonCode: reason,
          leaseToken: lease.token,
          candidateVersion: lease.candidate_version,
        })
      }
      void posta.flush()
      setBatch({
        rejected: taken.leases.map((one) => one.detection_id),
        skipped: wanted.length - taken.leases.length + (ticked.size - wanted.length),
        undoUntil: Date.now() + 5000,
      })
      setTicked(new Set())
    } catch (error) {
      setBatchFailure(
        error instanceof ServiceError ? explainService(t, error.code, error.message) : t('common.unreachable'),
      )
    }
  }

  function undoBatch(): void {
    if (batch === null) return
    for (const detectionId of batch.rejected) posta.add({ kind: 'undo', detectionId })
    void posta.flush()
    setBatch(null)
    setReloads((was) => was + 1)
  }

  const base = `/inspections/${inspectionId}`

  return (
    <div className="anomalies-view">
      <section className="anomalies-main" aria-label={t('anomalies.list')}>
        <div className="anomalies-bar">
          <div className="anomalies-statuses" role="group" aria-label={t('anomalies.status')}>
            {STATUSES.map((one) => (
              <button
                key={one.key}
                type="button"
                aria-pressed={filters.status === one.id}
                onClick={() => filter({ status: one.id })}
              >
                {t(one.key)} <span className="tenue t-num">{counts[one.count]}</span>
              </button>
            ))}
          </div>
          <div className="anomalies-ranks" role="group" aria-label={t('anomalies.rank')}>
            <button type="button" aria-pressed={filters.rank === null} onClick={() => filter({ rank: null })}>
              {t('anomalies.anyRank')}
            </button>
            {[1, 2, 3].map((level) => {
              const rank = rankOf(level)
              return (
                <button
                  key={level}
                  type="button"
                  aria-pressed={filters.rank === level}
                  onClick={() => filter({ rank: level })}
                  style={{ color: `var(--${rank.tone})` }}
                >
                  <span className="glifo" aria-hidden="true">
                    {rank.glyph}
                  </span>{' '}
                  {rank.name}
                </button>
              )
            })}
          </div>
          <label className="anomalies-search">
            <span className="solo-lettori">{t('anomalies.search')}</span>
            <input
              type="search"
              placeholder={t('anomalies.search')}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
        </div>
        <p className="tenue t-num anomalies-count" role="status">
          {t('anomalies.count', { shown: rows.length, total: found?.total ?? 0 })}
        </p>
        {(ticked.size > 0 || batch !== null || batchFailure !== null || refusals.length > 0) && (
          <div className="anomalies-batch" role="region" aria-label={t('anomalies.batch')}>
            {ticked.size > 0 && (
              <>
                <span>{t('anomalies.ticked', { n: ticked.size })}</span>
                <select aria-label={t('anomalies.batchReason')} value={reason} onChange={(event) => setReason(event.target.value)}>
                  <option value="">{t('anomalies.batchReason')}</option>
                  {reasons.map((one) => (
                    <option key={one.code} value={one.code}>
                      {explainReason(t, one.code, one.label)}
                    </option>
                  ))}
                </select>
                <button type="button" disabled={reason === ''} onClick={() => void rejectTicked()}>
                  {t('anomalies.rejectTicked', { n: ticked.size })}
                </button>
                <button type="button" className="anomalies-clear" onClick={() => setTicked(new Set())}>
                  {t('anomalies.untick')}
                </button>
              </>
            )}
            {batch !== null && (
              <span role="status">
                {t('anomalies.batchDone', { n: batch.rejected.length })}
                {batch.skipped > 0 && <> · {t('anomalies.batchSkipped', { n: batch.skipped })}</>}{' '}
                <button type="button" onClick={undoBatch}>
                  {t('anomalies.batchUndo')}
                </button>
              </span>
            )}
            {batchFailure !== null && <span role="alert">{batchFailure}</span>}
            {refusals.map((one, index) => (
              <span key={`${one.code}:${index}`} role="alert">
                {explainService(t, one.code, one.message)}
              </span>
            ))}
          </div>
        )}
        <div className="anomalies-scroll">
          <table className="anomalies-table">
            <thead>
              <tr>
                <th className="anomalies-tick" />
                <th>{t('anomalies.colRank')}</th>
                <th>{t('anomalies.colWhat')}</th>
                <th>{t('anomalies.colElement')}</th>
                <th className="t-num">{t('anomalies.colMargin')}</th>
                <th>{t('anomalies.colFrame')}</th>
                <th>{t('anomalies.colState')}</th>
                <th>{t('anomalies.colWho')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((one) => (
                <tr
                  key={one.detection_id}
                  className={one.detection_id === chosen ? 'on' : undefined}
                  aria-selected={one.detection_id === chosen}
                  onClick={() => setChosen(one.detection_id)}
                >
                  <td className="anomalies-tick" onClick={(event) => event.stopPropagation()}>
                    {one.status === 'waiting' && (
                      <input
                        type="checkbox"
                        aria-label={t('anomalies.tick', { code: one.code })}
                        checked={ticked.has(one.detection_id)}
                        onClick={(event) => tick(one, event.shiftKey)}
                        onChange={() => undefined}
                      />
                    )}
                  </td>
                  <td>
                    <Severity level={one.status === 'confirmed' ? one.final_level : one.level} />
                  </td>
                  <td>
                    <span className="anomalies-what">{one.description === '' ? one.code : one.description}</span>{' '}
                    <span className="tenue">{one.code}</span>
                  </td>
                  <td className="t-num">
                    {one.external_element_id === null ? (
                      <span className="tenue">—</span>
                    ) : (
                      <>
                        {one.external_element_id} <span className="tenue">{one.element_type}</span>
                      </>
                    )}
                  </td>
                  <td className="t-num">
                    <MarginCell row={one} />
                  </td>
                  <td className="anomalies-frame">
                    {one.photo_filename ?? <span className="tenue">{t('anomalies.noFrame')}</span>}
                  </td>
                  <td>
                    <StateLabel row={one} />
                  </td>
                  <td className="tenue anomalies-who">
                    {one.decided_by !== null && one.decided_at !== null
                      ? `${one.decided_by} · ${moment(one.decided_at)}`
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && found !== null && (
            // Che cosa e' successo e che cosa fare (GI-281): con un filtro acceso
            // lo si toglie da qui; senza filtri, le anomalie nascono al banco.
            <EmptyState
              title={t('anomalies.none')}
              action={filters.status !== null || filters.rank !== null || filters.q ? t('anomalies.noneFiltered') : t('anomalies.noneAtAll')}
            >
              {filters.status !== null || filters.rank !== null || filters.q ? (
                <button type="button" onClick={() => setFilters({ status: null, rank: null, q: null })}>
                  {t('anomalies.clearFilters')}
                </button>
              ) : (
                <Link to={`/inspections/${inspectionId}/queue`}>{t('pool.openQueue')}</Link>
              )}
            </EmptyState>
          )}
          {found !== null && rows.length < found.total && (
            <button type="button" className="anomalies-more" onClick={() => setOffset(rows.length)}>
              {t('anomalies.loadMore', { n: Math.min(api.PAGE, found.total - rows.length) })}
            </button>
          )}
        </div>
      </section>

      <aside className="anomalies-side" aria-label={t('anomalies.detail')}>
        {current === null ? (
          <Overview inspectionId={inspectionId} rows={rows} counts={found?.counts ?? null} onPick={setChosen} />
        ) : (
          <>
            <h2 className="anomalies-title">
              {current.description === '' ? current.code : current.description}
            </h2>
            <p className="tenue">{current.code}</p>
            {current.thumbnail_url !== null && (
              <img className="anomalies-thumb" src={current.thumbnail_url} alt="" />
            )}
            <dl className="anomalies-facts">
              <dt>{t('anomalies.colRank')}</dt>
              <dd>
                <Severity level={current.status === 'confirmed' ? current.final_level : current.level} />
                {current.status === 'confirmed' && current.final_level !== current.level && (
                  <span className="tenue"> {t('anomalies.proposed', { rank: rankOf(current.level).name })}</span>
                )}
              </dd>
              <dt>{t('anomalies.colState')}</dt>
              <dd>
                <StateLabel row={current} />
                {current.decided_by !== null && current.decided_at !== null && (
                  <span className="tenue">
                    {' '}
                    {t('anomalies.decidedBy', { who: current.decided_by, when: moment(current.decided_at) })}
                  </span>
                )}
              </dd>
              <dt>{t('anomalies.source')}</dt>
              <dd>
                {current.source}
                {current.confidence !== null && (
                  <span className="tenue"> · {decimal(current.confidence, 2)}</span>
                )}
              </dd>
              <dt>{t('anomalies.colElement')}</dt>
              <dd className="t-num">
                {current.external_element_id === null ? '—' : `${current.external_element_id} ${current.element_type ?? ''}`}
              </dd>
              <dt>{t('anomalies.colFrame')}</dt>
              <dd className="anomalies-frame">{current.photo_filename ?? t('anomalies.noFrame')}</dd>
            </dl>
            {!current.exportable && <p className="tenue">{t('anomalies.notExportable')}</p>}
            <HistoryList inspectionId={inspectionId} detectionId={current.detection_id} />
            <p className="anomalies-links">
              <Link to={`${base}/candidates/${current.detection_id}`}>
                {current.status === 'waiting' ? t('anomalies.decide') : t('anomalies.openDesk')}
              </Link>
              {current.element_id !== null && (
                <Link to={`${base}/elements/${current.element_id}`}>{t('anomalies.openElement')}</Link>
              )}
              {current.photo_filename !== null && (
                <Link to={`${base}/search?q=${encodeURIComponent(current.photo_filename)}`}>
                  {t('anomalies.openPhoto')}
                </Link>
              )}
            </p>
          </>
        )}
      </aside>
    </div>
  )
}

/** La storia, passo per passo: dalla nascita a ogni verdetto, ritiri compresi.
 *  Prima stava nel giornale, che legge solo chi amministra (GI-251). */
function HistoryList({ inspectionId, detectionId }: { inspectionId: string; detectionId: string }): JSX.Element | null {
  const t = useT()
  const [events, setEvents] = useState<api.AnomalyEvent[] | null>(null)
  useEffect(() => {
    let alive = true
    setEvents(null)
    api
      .history(inspectionId, detectionId)
      .then((found) => alive && setEvents(found.events))
      .catch(() => alive && setEvents([]))
    return () => {
      alive = false
    }
  }, [inspectionId, detectionId])
  if (events === null || events.length === 0) return null
  return (
    <section className="anomalies-history" aria-label={t('anomalies.history')}>
      <h3>{t('anomalies.history')}</h3>
      <ol>
        {events.map((one, index) => (
          <li key={`${one.kind}:${one.at}:${index}`}>
            <span className="t-num tenue">{moment(one.at)}</span>{' '}
            <span>{eventText(one, t)}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}

function eventText(one: api.AnomalyEvent, t: ReturnType<typeof useT>): string {
  const who = one.who ?? t('anomalies.someone')
  const rank = one.level === null ? '' : rankOf(one.level).name
  if (one.kind === 'predicted') return t('anomalies.eventPredicted', { rank })
  if (one.kind === 'declared') return t('anomalies.eventDeclared', { rank })
  if (one.kind === 'measured') return t('anomalies.eventMeasured', { rank })
  if (one.kind === 'withdrawn') return t('anomalies.eventWithdrawn', { who })
  if (one.kind === 'rejected')
    return t('anomalies.eventRejected', { who, reason: explainReason(t, one.reason_code, one.reason_label ?? one.reason_code ?? '') })
  return one.corrected ? t('anomalies.eventConfirmedCorrected', { who, rank }) : t('anomalies.eventConfirmed', { who, rank })
}

/** Il margine sulla norma, col tono della sua fascia: e' cio' che si legge nel
 *  banco, e in elenco mancava — «3,03 m» da solo non dice niente. */
function MarginCell({ row }: { row: api.AnomalyRow }): JSX.Element {
  const delta = marginOf(row.measured_distance_m ?? null, row.minimum_distance_m ?? null)
  if (delta === null) return <span className="tenue">—</span>
  const band = bandOf(delta)
  return (
    <span className="t-num" style={{ color: `var(--${band.tone})`, whiteSpace: 'nowrap' }} title={band.meaning}>
      Δ {delta > 0 ? '+' : ''}
      {decimal(delta, 2)} m
    </span>
  )
}

/**
 * Il pannello prima di scegliere: che cosa c'e' qui, non «scegli una riga».
 *
 * Riempiva il 38 % dell'altezza con una frase (S17, L-3). Ora dice la
 * commessa in tre modi — per rango, per stato e per sorgente, per elemento —
 * mostra la linea in piccolo con le anomalie, che si scelgono col dito, e i
 * fotogrammi che le inquadrano.
 */
function Overview({
  inspectionId,
  rows,
  counts,
  onPick,
}: {
  inspectionId: string
  rows: api.AnomalyRow[]
  counts: api.AnomalyCounts | null
  onPick: (detectionId: string) => void
}): JSX.Element {
  const t = useT()
  const byRank = new Map<number, number>()
  const bySource = new Map<string, number>()
  const byElement = new Map<string, { label: string; n: number }>()
  for (const one of rows) {
    const level = one.status === 'confirmed' && one.final_level !== null ? one.final_level : one.level
    if (level !== null) byRank.set(level, (byRank.get(level) ?? 0) + 1)
    bySource.set(one.source, (bySource.get(one.source) ?? 0) + 1)
    const key = one.element_id ?? '—'
    const label = one.external_element_id === null ? t('anomalies.noElement') : `${one.external_element_id} ${one.element_type ?? ''}`
    byElement.set(key, { label, n: (byElement.get(key)?.n ?? 0) + 1 })
  }
  const framed = rows.filter((one) => one.thumbnail_url !== null).slice(0, 12)
  return (
    <div className="anomalies-overview">
      <h2 className="anomalies-title">{t('anomalies.overview')}</h2>
      <dl className="anomalies-facts">
        <dt>{t('anomalies.colRank')}</dt>
        <dd>
          {[1, 2, 3].map((level) => (
            <span key={level} className="anomalies-count-chip">
              <Severity level={level} /> <span className="t-num">{decimal(byRank.get(level) ?? 0)}</span>
            </span>
          ))}
        </dd>
        <dt>{t('anomalies.colState')}</dt>
        <dd className="t-num">
          {counts === null
            ? '—'
            : `${decimal(counts.waiting)} ${t('anomalies.stateWaiting')} · ${decimal(counts.confirmed)} ${t('anomalies.stateConfirmedShort')} · ${decimal(counts.rejected)} ${t('anomalies.stateRejected')}`}
        </dd>
        <dt>{t('anomalies.source')}</dt>
        <dd className="t-num">{[...bySource].map(([source, n]) => `${source} ${decimal(n)}`).join(' · ') || '—'}</dd>
        <dt>{t('anomalies.colElement')}</dt>
        <dd className="t-num">
          {[...byElement.values()].map((one) => `${one.label} (${decimal(one.n)})`).join(' · ') || '—'}
        </dd>
      </dl>
      <div className="anomalies-minimap">
        <LineMap inspectionId={inspectionId} bare onMark={onPick} />
      </div>
      {framed.length > 0 && (
        <ul className="anomalies-tiles" aria-label={t('anomalies.framesWith')}>
          {framed.map((one) => (
            <li key={one.detection_id}>
              <button type="button" onClick={() => onPick(one.detection_id)} title={`${one.code} · ${one.photo_filename ?? ''}`}>
                <img src={one.thumbnail_url ?? ''} alt="" loading="lazy" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Lo stato in parole: da decidere, confermata col rango, respinta col motivo. */
function StateLabel({ row }: { row: AnomalyRow }): JSX.Element {
  const t = useT()
  if (row.status === 'waiting') return <span className="anomalies-state waiting">{t('anomalies.stateWaiting')}</span>
  if (row.status === 'confirmed') {
    return (
      <span className="anomalies-state confirmed">
        {t('anomalies.stateConfirmed', { rank: rankOf(row.final_level).name })}
      </span>
    )
  }
  return (
    <span className="anomalies-state rejected">
      {t('anomalies.stateRejected')}
      {row.reason_label !== null && (
        <span className="tenue">: {explainReason(t, row.reason_code, row.reason_label)}</span>
      )}
    </span>
  )
}
